import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Grid } from '@mui/material';
import { filter, sortBy } from 'lodash';

import { getCategoryFromPathname } from '../utils/analytics/navigation';
import { trackSentryError } from '../utils/analytics/sentry';
import { generateTab, handleApiError as handleApiErrorUtil } from '../utils/api';
import {
  trackClickGenerateTabConfirmation,
  trackClickGeneratedTab,
} from '../utils/analytics/analytics';
import { registerAsync } from '../utils/auth';
import {
  SECTIONS,
  TAB_GENERATION_DESCRIPTIONS,
  getTabGenerationCost,
} from '../utils/const';
import { useFirebaseLoginState } from '../utils/hooks';
import { RootState } from '../utils/store';
import { getTranslationKey } from '../utils/translationUtils';
import {
  CampaignSection as CampaignSectionType,
  Item,
  LoadingStatus,
  RequestType,
  SectionName,
  TabName,
} from '../utils/types';
import { CampaignSectionItem } from './CampaignSectionItem';
import { CampaignSectionTabs } from './CampaignSectionTabs';
import { CampaignMetadataSectionComponent } from './minicomponents/minicomponents';
import AlertPopup from './popups/AlertPopup';
import LoadingPopup from './popups/LoadingPopup';
import RegeneratePopup from './popups/RegeneratePopup';
import RegisterPopup from './popups/RegisterPopup';
import UpgradePopup from './popups/UpgradePopup';

interface CampaignSectionProps {
  sectionName: SectionName;
}

const CampaignSection = ({ sectionName }: CampaignSectionProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabName | null>(null);
  const [generatePopupOpen, setGeneratePopupOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [tabToGenerate, setTabToGenerate] = useState<TabName | null>(null);
  const { userId, isAnonymous } = useFirebaseLoginState();
  const [isUpgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const [isRegisterPopupOpen, setIsRegisterPopupOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const location = useLocation();

  const state = useSelector((state: RootState) => state.appState);
  const creditsLeft = state?.subcription?.credits || 0;
  const selectedCampaign = useSelector(
    (state: RootState) => state.appState.selectedCampaign
  );

  const currentSection: CampaignSectionType | undefined = SECTIONS.find(
    (section) => section.name === sectionName
  );
  const hasTabs = !!currentSection?.tabs && !!currentSection.tabs?.length;

  useEffect(() => {
    if (currentSection && currentSection.tabs && currentSection.tabs.length > 0) {
      // If activeTab is null or not a valid tab for the current section, set it to the first tab.
      // This ensures that on initial load or section change, a valid tab is active.
      const currentTabsNames = currentSection.tabs.map((t) => t.name);
      if (!activeTab || !currentTabsNames.includes(activeTab)) {
        setActiveTab(currentSection.tabs[0].name);
      }
    } else {
      // If the section has no tabs, activeTab should be null.
      setActiveTab(null);
    }
  }, [sectionName, currentSection, activeTab]);

  const getTabDescriptionKey = useCallback((): string | undefined => {
    if (!tabToGenerate || !sectionName) {
      return undefined;
    }
    return TAB_GENERATION_DESCRIPTIONS[sectionName]?.[tabToGenerate];
  }, [tabToGenerate, sectionName]);

  const tabDescriptionKeyForPopup = getTabDescriptionKey();

  const isTabRequireGeneration = (tabItems: RequestType[]) => {
    const allFetchedItemsCodes =
      selectedCampaign?.items?.map((item) => item.type) || [];
    return !tabItems.some((code) => allFetchedItemsCodes.includes(code));
  };

  const renderSectionItems = (itemTypes: RequestType[]) => {
    const sectionItemsObjects =
      selectedCampaign?.items &&
      (sortBy(
        filter(selectedCampaign.items, (item) => itemTypes.includes(item.type)),
        (item) => itemTypes.indexOf(item.type)
      ) as Item[]);

    return sectionItemsObjects?.map((item) => (
      <CampaignSectionItem
        key={item.id}
        item={item}
        sectionName={sectionName}
        tabName={activeTab}
      />
    ));
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabName) => {
    const tab = currentSection?.tabs?.find((tab) => tab.name === newValue);
    if (tab && isTabRequireGeneration(tab.items)) {
      setTabToGenerate(newValue);

      if (isAnonymous) {
        setIsRegisterPopupOpen(true);
      } else {
        setGeneratePopupOpen(true);
      }
    } else {
      // Track click on already generated tab
      trackClickGeneratedTab(sectionName, newValue);
      setActiveTab(newValue);
    }
  };

  const handleGenerateClose = () => {
    setGeneratePopupOpen(false);
  };

  const handleRegisterPopupClose = () => {
    setIsRegisterPopupOpen(false);
  };

  const handleRegisterClicked = async () => {
    setIsRegisterPopupOpen(false);
    try {
      const category = getCategoryFromPathname(location.pathname);
      await registerAsync(category);
    } catch (error) {
      trackSentryError('Registration error in CampaignSection', { error });
      console.error('Registration error:', error);
      setError(t('app.errors.registrationFailed'));
      setIsAlertOpen(true);
    }
  };

  const handleError = useCallback((error: unknown) => {
    handleApiErrorUtil({
      error,
      onSetUpgradePopupOpen: setUpgradePopupOpen,
      onSetErrorMessage: setError,
      onSetErrorAlertOpen: (isOpen) => {
        setIsAlertOpen(isOpen);
        if (!isOpen) setError(null);
      },
    });
  }, []);

  const handleGenerateSubmit = async (remarks: string) => {
    if (!userId || !selectedCampaign || !tabToGenerate) return;

    // Track tab generation confirmation
    trackClickGenerateTabConfirmation(
      sectionName,
      tabToGenerate,
      generationCost,
      state.creativity,
      remarks
    );

    setIsGenerating(true);
    setGeneratePopupOpen(false);

    const tabItems =
      currentSection?.tabs?.find((tab) => tab.name === tabToGenerate)?.items || [];

    try {
      await generateTab(userId, selectedCampaign.id!, state.session, tabItems, remarks);
      setActiveTab(tabToGenerate);
    } catch (error) {
      handleError(error);
    } finally {
      setIsGenerating(false);
      setTabToGenerate(null);
    }
  };

  const generationCost = getTabGenerationCost(tabToGenerate, sectionName);

  const popups = (
    <>
      <RegeneratePopup
        open={generatePopupOpen}
        onClose={handleGenerateClose}
        onSubmit={handleGenerateSubmit}
        title={t('app.popups.generate.titleTab', {
          title: t(`app.sectionsNames.${getTranslationKey(sectionName)}`),
          tab: tabToGenerate
            ? t(`app.tabsNames.${getTranslationKey(tabToGenerate)}`)
            : '',
        })}
        descriptionKey={tabDescriptionKeyForPopup}
        buttonText={t('app.popups.generate.buttonText', { count: generationCost })}
        generationCost={generationCost}
        creditsLeft={creditsLeft}
        isRemarksMandatory={false}
      />
      <RegisterPopup
        open={isRegisterPopupOpen}
        onClose={handleRegisterPopupClose}
        onRegisterClicked={handleRegisterClicked}
      />
      <LoadingPopup
        open={isGenerating}
        title={t('app.popups.loading.generatingTitle', {
          section: t(`app.sectionsNames.${getTranslationKey(sectionName)}`),
          tab: tabToGenerate
            ? t(`app.tabsNames.${getTranslationKey(tabToGenerate)}`)
            : '',
        })}
        initialText={t('app.popups.loading.initialText')}
      />
      <UpgradePopup
        open={isUpgradePopupOpen}
        onClose={() => setUpgradePopupOpen(false)}
        onError={handleError}
      />
      <AlertPopup
        open={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onSubmit={() => setIsAlertOpen(false)}
        alertText={error || ''}
        title={t('app.popups.error.title')}
        buttonText={t('app.buttons.ok')}
      />
    </>
  );

  // Check if section has tabs
  if (hasTabs && currentSection) {
    const tabs = currentSection.tabs!;
    const activeTabItems =
      tabs.find((tab) => tab.name === activeTab)?.items || tabs[0].items;

    const isTabItemsLoading = (tabItems: RequestType[]) => {
      return tabItems.some((code) =>
        selectedCampaign?.items?.some(
          (item) => item.type === code && item.status === LoadingStatus.LOADING
        )
      );
    };

    const isTabItemsError = (tabItems: RequestType[]) => {
      return tabItems.some((code) =>
        selectedCampaign?.items?.some(
          (item) => item.type === code && item.status === LoadingStatus.FAILED
        )
      );
    };

    return (
      <Grid container spacing={3} alignContent="flex-start">
        {/* Show Metadata Summary section*/}
        {sectionName === SectionName.Summary && selectedCampaign && (
          <Grid size={12}>
            <CampaignMetadataSectionComponent
              createdAt={selectedCampaign.created_at}
              language={selectedCampaign.language}
              creativity={selectedCampaign.creativity}
              isEvenSpacing={false}
            />
          </Grid>
        )}
        <Grid size={12}>
          <CampaignSectionTabs
            tabs={tabs.map((tab) => ({
              name: tab.name,
              isRequireGeneration: isTabRequireGeneration(tab.items),
              isLoading: isTabItemsLoading(tab.items),
              isError: isTabItemsError(tab.items),
            }))}
            activeTab={activeTab || tabs[0].name}
            onTabChange={handleTabChange}
          />
        </Grid>
        {renderSectionItems(activeTabItems)}
        {popups}
      </Grid>
    );
  }

  // Finally, if it's only one section, render the section
  return (
    <Grid size={12} container gap={3} alignContent="flex-start">
      {currentSection &&
        renderSectionItems(currentSection.tabs?.flatMap((tab) => tab.items) || [])}
      {popups}
    </Grid>
  );
};

export default React.memo(CampaignSection);
